import { io, Socket } from 'socket.io-client';
import toast from 'react-hot-toast';

class SocketService {
  private socket: Socket | null = null;
  private isConnected: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000; // 1 segundo

  constructor() {
    // Adicionar ao window para uso global
    if (typeof window !== 'undefined') {
      (window as any).socketService = this;
    }
  }

  /**
   * Conectar ao servidor WebSocket
   */
  connect(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        console.log('🔌 Conectando ao WebSocket...');
        
        const backendUrl = import.meta.env.VITE_BACKEND_URL || import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3002';
        this.socket = io(backendUrl, {
          auth: {
            token: token
          },
          transports: ['websocket', 'polling'],
          timeout: 10000,
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: this.reconnectDelay
        });

        this.setupEventListeners();
        
        this.socket.on('connect', () => {
          console.log('✅ Conectado ao WebSocket');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          
          // Adicionar ao window para uso global
          if (typeof window !== 'undefined') {
            (window as any).socket = this.socket;
          }
          
          toast.success('Conectado ao sistema em tempo real');
          resolve();
        });

        this.socket.on('connect_error', (error) => {
          console.error('❌ Erro de conexão WebSocket:', error);
          this.isConnected = false;
          reject(error);
        });

        this.socket.on('disconnect', (reason) => {
          console.log('🔌 Desconectado do WebSocket:', reason);
          this.isConnected = false;
          
          if (reason === 'io server disconnect') {
            // Reconectar manualmente se o servidor desconectou
            this.socket?.connect();
          }
        });

      } catch (error) {
        console.error('❌ Erro ao criar conexão WebSocket:', error);
        reject(error);
      }
    });
  }

  /**
   * Configurar listeners de eventos
   */
  private setupEventListeners() {
    if (!this.socket) return;

    // Evento de posição atualizada (confirmação)
    this.socket.on('posicao_atualizada', (data) => {
      console.log('📍 Posição atualizada confirmada:', data);
    });

    // Evento de mudança de status
    this.socket.on('status_atualizado', (data) => {
      console.log('🔄 Status atualizado:', data);
      toast.success(`Status atualizado: ${data.novoStatus}`);
    });

    // Evento de erro
    this.socket.on('error', (error) => {
      console.error('❌ Erro no WebSocket:', error);
      toast.error('Erro na conexão em tempo real');
    });

    // Evento de reconexão
    this.socket.on('reconnect', (attemptNumber) => {
      console.log('🔄 Reconectado ao WebSocket, tentativa:', attemptNumber);
      toast.success('Reconectado ao sistema em tempo real');
    });

    // Evento de falha na reconexão
    this.socket.on('reconnect_failed', () => {
      console.error('❌ Falha na reconexão WebSocket');
      toast.error('Falha na conexão em tempo real');
    });
  }

  /**
   * Entrar na sala de uma OS específica
   */
  joinOSRoom(codigoAcesso: string): void {
    if (!this.socket || !this.isConnected) {
      console.warn('⚠️ WebSocket não conectado');
      return;
    }

    console.log('🚪 Entrando na sala da OS:', codigoAcesso);
    this.socket.emit('join_os_room', { codigo_acesso: codigoAcesso });
  }

  /**
   * Sair da sala da OS
   */
  leaveOSRoom(): void {
    if (!this.socket || !this.isConnected) {
      return;
    }

    console.log('🚪 Saindo da sala da OS');
    // O socket automaticamente sai da sala ao desconectar
  }

  /**
   * Emitir atualização de posição
   */
  emitPositionUpdate(data: {
    osId: number;
    faseId: number;
    lat: number;
    lng: number;
    velocidade?: number;
    direcao?: number;
    precisao?: number;
    altitude?: number;
  }): void {
    if (!this.socket || !this.isConnected) {
      console.warn('⚠️ WebSocket não conectado');
      return;
    }

    console.log('📍 Emitindo atualização de posição:', data);
    this.socket.emit('atualizar_posicao', data);
  }

  /**
   * Desconectar do WebSocket
   */
  disconnect(): void {
    if (this.socket) {
      console.log('🔌 Desconectando do WebSocket...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      
      // Remover do window
      if (typeof window !== 'undefined') {
        delete (window as any).socket;
        delete (window as any).socketService;
      }
    }
  }

  /**
   * Verificar se está conectado
   */
  getConnectionStatus(): boolean {
    return this.isConnected;
  }

  /**
   * Obter instância do socket
   */
  getSocket(): Socket | null {
    return this.socket;
  }
}

// Criar instância única
const socketService = new SocketService();

export default socketService;
